/**
 * @fileoverview Recurring donation section component for Click&Pledge.
 * Handles recurring payment options, frequency selection, and related form state management.
 */

import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    bindAmountSection,
    bindConvenienceFeeAmount,
    bindConvenienceFeeSelected,
    bindDonationItems,
    bindFinalDonationAmount,
    bindInstallmentChargeValue,
    bindIsInstallmentTouched,
    bindMeetFormData,
    bindMultiDonationOrderItems,
    bindMultiPayTotalTaxDeduct,
    bindNoOfPeriodicity,
    bindPaymentTypeMethod,
    bindPeriodicityEndDate,
    bindRecurringMethod,
    bindRecurringSelection,
    bindShowOneTimePayOption
} from '../../../actions';
import { checkDateExpired, cnpGetDisplayLabelForRecurring, filterButtonsToShowSix, getButtonStyles, getRecurringKeyToValue, getRecurringValueToKey } from '../utility/util';


/**
 * Recurring section component that manages recurring donation options.
 * Handles frequency selection (one-time, monthly, etc.), updates related form state,
 * and manages donation amounts based on recurring selections.
 *
 * @param {Object} props - Component props
 * @param {Object} props.recurringValidator - Validation reference for recurring fields
 * @returns {ReactElement} The recurring section component
 */
function RecurringSection(props) {
    // Style objects to avoid inline styles
    const styles = {
        formWrapper: { alignItems: 'center' },
        recurringBtnsFullWidth: { width: '100%' },
        recurringBtnsAuto: { width: 'auto' },
        buttonNoWrap: { whiteSpace: 'nowrap' },
        errorContainer: { paddingLeft: '45%' },
        paymentHeader: { textAlign: 'center',
            fontWeight: '600' }
    };

    const recurring = useSelector(
    state => state['features/clickandpledge']?.recurringSection || {}
    );
    const recurringSelection = useSelector(
    state => state['features/clickandpledge']?.recurringSelection || ''
    );
    const installments = useSelector(
    state => state['features/clickandpledge']?.installementSelected || ''
    );

    const donationamount = useSelector(
    state => state['features/clickandpledge']?.donationAmountSelected || ''
    );
    const convFee = useSelector(
    state => state['features/clickandpledge']?.convenienceFeeAmount || ''
    );
    const meetformData = useSelector(
    state => state['features/clickandpledge']?.meetFormData || {}
    );
    const IsConvFeeSelected = useSelector(
    state => state['features/clickandpledge']?.convenienceFeeSelected || false
    );
    const formSettings = useSelector(
    state => state['features/clickandpledge']?.formSettings || {}
    );


    const dispatch = useDispatch();

    useEffect(() => {

        if (
            !meetformData.formdata
      || (meetformData.formdata
        && meetformData.formdata[0]
        && !meetformData.formdata[0].recurrings && recurring.DefaultFrequency)
        ) {

            dispatch(
        bindMeetFormData({
            formdata: meetformData.formdata
                ? meetformData.formdata.map(q => {
                    return {
                        ...q,
                        recurrings: getRecurringKeyToValue(recurring.DefaultFrequency)
                    };
                })
                : [
                    {
                        recurrings: getRecurringKeyToValue(
                    recurring.DefaultFrequency
                        )
                    }
                ]
        })
            );

            dispatch(
        bindRecurringSelection(
          getRecurringKeyToValue(recurring.DefaultFrequency)
        )
            );


        }

    }, [ recurring ]);


    /**
     * Memoized value for current recurring selection from form data.
     * Finds the recurring option from the form data array.
     */
    const rValue = useMemo(() => {
        if (meetformData.formdata) {
            return meetformData.formdata.find(obj => obj.recurrings);
        }

        return [];
    }, [ meetformData ]);

    const newRecItem = recurring.PaymentOptionsSchedulingDetails
        ? recurring.PaymentOptionsSchedulingDetails
        : [];

    /**
     * Handles recurring option change via button selection.
     * Updates form data, amount sections, and related state based on selection.
     *
     * @param {Event} e - Button click event
     * @param {number} i - Index of the selected recurring option
     */
    const onChangeRecurring = e => {

        const defaultRec = getRecurringKeyToValue(e.currentTarget.value);

        dispatch(
      bindMeetFormData({
          formdata: meetformData.formdata
              ? meetformData.formdata.map(q => {
                  return {
                      ...q,
                      recurrings: defaultRec
                  };
              })
              : [ { recurrings: defaultRec } ]
      })
        );

        const defaultAmt = newRecItem.filter(opt => opt.Key === e.currentTarget.value);
        const defaultAmtSec = defaultAmt.length && defaultAmt[0].Value;

        if (defaultAmt && Object.keys(defaultAmtSec).length) {

            let updatedAmtSecArr = defaultAmtSec.DonationItemList.filter(item => !checkDateExpired(item.StartDate, item.EndDate));

            updatedAmtSecArr = filterButtonsToShowSix(updatedAmtSecArr);
            if (defaultRec === 'One Time Only' && !updatedAmtSecArr.length) {
                dispatch(bindShowOneTimePayOption(true));
            } else {
                dispatch(bindShowOneTimePayOption(false));
            }

            const defaultAmtSecArr = {
                ...defaultAmtSec,
                DonationItemList: updatedAmtSecArr

            };

            dispatch(bindAmountSection(defaultAmtSecArr));
            dispatch(bindDonationItems(defaultAmtSec.DonationItemList));

            // Use RecurringMethod as paymentTypeMethod for recurring payments, fallback to PaymentType
            const paymentType = defaultRec !== 'One Time Only' && defaultAmtSec.RecurringMethod
                ? defaultAmtSec.RecurringMethod
                : defaultAmtSec.PaymentType;

            dispatch(bindPaymentTypeMethod(paymentType));
            dispatch(bindRecurringMethod(defaultAmtSec.RecurringMethod));
            dispatch(bindNoOfPeriodicity(defaultAmtSec.PeriodicityNoOfPeriods));
            dispatch(bindPeriodicityEndDate(defaultAmtSec.PeriodicityEndDate));
        }

        dispatch(bindRecurringSelection(defaultRec));
        dispatch(bindIsInstallmentTouched(false));

        dispatch(bindInstallmentChargeValue(''));
        dispatch(bindConvenienceFeeAmount(''));
        dispatch(bindConvenienceFeeSelected(false));
        dispatch(bindMultiDonationOrderItems([]));
        dispatch(bindMultiPayTotalTaxDeduct(0));
        if (e.currentTarget.value === 'One Time Only') {
            if (IsConvFeeSelected) {

                dispatch(
          bindFinalDonationAmount(
            Math.round(
              ((parseFloat(donationamount) + convFee) + Number.EPSILON) * 100
            ) / 100
          )
                );
            } else {
                dispatch(bindFinalDonationAmount(donationamount));
            }
        } else if (installments && IsConvFeeSelected) {
            dispatch(
          bindFinalDonationAmount(
            Math.round(
              ((donationamount / installments) + convFee + Number.EPSILON) * 100
            ) / 100
          )
            );
        } else if (installments && !IsConvFeeSelected) {
            dispatch(
          bindFinalDonationAmount(
            Math.round(((donationamount / installments) + Number.EPSILON) * 100)
              / 100
          )
            );
        } else {
            dispatch(bindFinalDonationAmount(donationamount));
        }
    };

    /**
     * Handles recurring option change via dropdown selection.
     * Similar to onChangeRecurring but for dropdown interface when more than 3 options.
     *
     * @param {Event} e - Select change event
     */
    const onChangeRecurringSelect = e => {
        const defaultRec = getRecurringKeyToValue(e.target.value);

        dispatch(
      bindMeetFormData({
          formdata: meetformData.formdata
              ? meetformData.formdata.map(q => {
                  return {
                      ...q,
                      recurrings: defaultRec
                  };
              })
              : [ { recurrings: defaultRec } ]
      })
        );


        dispatch(bindRecurringSelection(defaultRec));
        dispatch(bindIsInstallmentTouched(false));
        dispatch(bindInstallmentChargeValue(''));
        dispatch(bindConvenienceFeeAmount(''));
        dispatch(bindConvenienceFeeSelected(false));
        dispatch(bindMultiDonationOrderItems([]));
        dispatch(bindMultiPayTotalTaxDeduct(0));

        const defaultAmt = newRecItem.filter(opt => opt.Key === e.target.value);
        const defaultAmtSec = defaultAmt.length && defaultAmt[0].Value;

        if (defaultAmt && Object.keys(defaultAmtSec).length) {


            let updatedAmtSecArr = defaultAmtSec.DonationItemList.filter(item => !checkDateExpired(item.StartDate, item.EndDate));

            updatedAmtSecArr = filterButtonsToShowSix(updatedAmtSecArr);
            if (defaultRec === 'One Time Only' && !updatedAmtSecArr.length) {
                dispatch(bindShowOneTimePayOption(true));
            } else {
                dispatch(bindShowOneTimePayOption(false));
            }
            const defaultAmtSecArr = {
                ...defaultAmtSec,
                DonationItemList: updatedAmtSecArr

            };

            dispatch(bindAmountSection(defaultAmtSecArr));
            dispatch(bindDonationItems(defaultAmtSec.DonationItemList));

            // Use RecurringMethod as paymentTypeMethod for recurring payments, fallback to PaymentType
            const paymentType = defaultRec !== 'One Time Only' && defaultAmtSec.RecurringMethod
                ? defaultAmtSec.RecurringMethod
                : defaultAmtSec.PaymentType;

            dispatch(bindPaymentTypeMethod(paymentType));
            dispatch(bindRecurringMethod(defaultAmtSec.RecurringMethod));
            dispatch(bindNoOfPeriodicity(defaultAmtSec.PeriodicityNoOfPeriods));
            dispatch(bindPeriodicityEndDate(defaultAmtSec.PeriodicityEndDate));
        }

        if (e.target.value === 'One Time Only') {
            if (IsConvFeeSelected) {
                dispatch(
          bindFinalDonationAmount(
            Math.round(
              ((parseFloat(donationamount) + convFee) + Number.EPSILON) * 100
            ) / 100
          )
                );
            } else {
                dispatch(bindFinalDonationAmount(donationamount));
            }
        } else if (IsConvFeeSelected && installments) {
            dispatch(
          bindFinalDonationAmount(
            Math.round(
              ((donationamount / installments) + convFee + Number.EPSILON) * 100
            ) / 100
          )
            );
        } else if (!IsConvFeeSelected && installments) {
            dispatch(
          bindFinalDonationAmount(
            Math.round(((donationamount / installments) + Number.EPSILON) * 100)
              / 100
          )
            );
        } else if (donationamount) {
            // For recurring payments without installments, use the full donation amount
            dispatch(bindFinalDonationAmount(donationamount));
        }
        props.recurringValidator.current.showMessageFor('recurrings');
    };

    const amountSelectedStyles = getButtonStyles(
                formSettings.AmountButtonSettings && formSettings.AmountButtonSettings.FB_selectedButtons
    );

    const selectedButtonStyles = amountSelectedStyles ? {
        color: '#020b13',
        borderColor: '#020b13',
        backgroundColor: '#c1c7cc'
    } : {};

    return (
        <>
            <div
                className = 'form_input_wrapper  d-flex justify-content-between'
                style = { styles.formWrapper }>


                {/** Recurring Buttons */}
                <div
                    className = { `recurring_btns_wrapper row-cols-${newRecItem && newRecItem.length <= 3 ? newRecItem.length : 3}` }
                    style = { newRecItem && newRecItem.length <= 3 ? styles.recurringBtnsFullWidth : styles.recurringBtnsAuto }>
                    {newRecItem && newRecItem.length <= 3
            && newRecItem.map((item, index) => (
                <div
                    className = 'dw-col'
                    key = { index }>
                    <button
                        className = { `full_width section_btn rec-buttons${
                            recurringSelection === getRecurringKeyToValue(item.Key) ? ' selected' : ''
                        }` }
                        name = 'recurrings'
                        onClick = { e => onChangeRecurring(e) }
                        onMouseUp = { () =>
                            props.recurringValidator.current.showMessageFor(
                        'recurrings'
                            )
                        }
                        style = { recurringSelection === getRecurringKeyToValue(item.Key) ? selectedButtonStyles : null }
                        type = 'button'
                        value = { item.Key }>
                        {cnpGetDisplayLabelForRecurring(item.Key)}
                    </button>


                </div>
            ))}
                </div>

                {/** Recurring Select dropdown */}
                {newRecItem && newRecItem.length > 3 && (
                    <>
                        <div className = 'rec_int_input'>
                            <select
                                className = 'form-input'
                                id = 'iterval'
                                name = 'recurrings'
                                onChange = { e => onChangeRecurringSelect(e) }
                                value = {
                                    recurringSelection
                                        ? getRecurringValueToKey(recurringSelection)
                                        : ''
                                }>
                                <option value = ''>--Choose Periodicity--</option>
                                {newRecItem.map((item, index) => (
                                    <option
                                        key = { index }
                                        value = { item.Key }>
                                        {cnpGetDisplayLabelForRecurring(item.Key)}
                                    </option>
                                ))}
                            </select>
                            <span className = 'select_arrow'>
                                <svg
                                    height = '22'
                                    viewBox = '0 0 24 24'
                                    width = '22'
                                    xmlns = 'http://www.w3.org/2000/svg'>
                                    <path
                                        d = 'M0 0h24v24H0z'
                                        fill = 'none' />
                                    <path d = 'M12 13.172l4.95-4.95 1.414 1.414L12 16 5.636 9.636 7.05 8.222z' />
                                </svg>
                            </span>
                        </div>


                    </>
                )}
            </div>
            <div
                className = 'rec-error'
                style = { styles.errorContainer }>
                {props.recurringValidator.current.message(
                    'recurrings',
                    rValue && rValue.recurrings,
                    'required',
                    { className: 'text-danger' }
                )}
            </div>
            {formSettings.PaymentSelectionHeader ? <p
                style = { styles.paymentHeader }>{formSettings.PaymentSelectionHeader}</p> : null}
        </>
    );
}
export default RecurringSection;
